import { GROUPS, RAILS, SQUARES, UTILS, priceOf, type Group, type MonopolyView, type Sq } from "../../../shared/games/monopoly";
import type { Palette } from "../../monopoly3d/textures";

/** a colour for every seat – the base of the figure on the board, the owner plates and the cards in the HUD */
export const SEAT_COLOR = ["#ef4444", "#2f7bff", "#22c55e", "#f59e0b"];
export const GROUP_COLOR: Record<Group, string> = { brown: "#9a5f32", lblue: "#74d0ff", pink: "#f56fb4", orange: "#ff9a3c", red: "#ef3e4a", yellow: "#ffd428", green: "#27c25e", dblue: "#2b59e6" };
export const kr = (n: number) => `${Math.round(n).toLocaleString("da-DK")} kr.`;
export const DIE_FACE = ["", "⚀", "⚁", "⚂", "⚃", "⚄", "⚅"];

const HEX = /^#[0-9a-f]{6}$/i;
/** the board theme the viewer has equipped (shop item with data {light, dark, accent}) */
export function paletteOf(avatar: Record<string, any> | null | undefined): Palette | undefined {
  const d = avatar?.boardtheme?.data;
  if (d && HEX.test(d.light) && HEX.test(d.dark) && HEX.test(d.accent)) return { light: d.light, dark: d.dark, accent: d.accent };
  return undefined;
}

/** what a field is, in a few Danish words */
export function fieldKind(q: Sq): string {
  switch (q.t) {
    case "prop": return "Gade";
    case "rail": return "Jernbane";
    case "util": return "Forsyning";
    case "chance": return "Chancekort";
    case "chest": return "Fælleskassen";
    case "tax": return "Skat";
    case "go": return "Start";
    case "jail": return "Fængsel";
    case "parking": return "Fri parkering";
    case "gojail": return "Gå i fængsel";
  }
}

export interface RentRow { label: string; value: string; on?: boolean }
/** the rent table of a deed; `on` marks the row that is in force right now */
export function rentRows(b: MonopolyView, i: number): RentRow[] {
  const q = SQUARES[i]!;
  const o = b.owner[i];
  if (q.t === "prop") {
    const h = b.houses[i] ?? 0;
    const full = o !== null && o !== undefined && GROUPS[q.group].every((x) => b.owner[x] === o);
    return [
      { label: "Leje", value: kr(q.rent[0]!), on: h === 0 && !full && o !== null && o !== undefined },
      { label: "Med hele farven", value: kr(q.rent[0]! * 2), on: h === 0 && full },
      { label: "1 hus", value: kr(q.rent[1]!), on: h === 1 },
      { label: "2 huse", value: kr(q.rent[2]!), on: h === 2 },
      { label: "3 huse", value: kr(q.rent[3]!), on: h === 3 },
      { label: "4 huse", value: kr(q.rent[4]!), on: h === 4 },
      { label: "Hotel", value: kr(q.rent[5]!), on: h === 5 },
    ];
  }
  if (q.t === "rail") {
    const n = o === null || o === undefined ? 0 : RAILS.filter((x) => b.owner[x] === o).length;
    return [1, 2, 3, 4].map((k) => ({ label: k === 1 ? "1 jernbane" : `${k} jernbaner`, value: kr(25 * 2 ** (k - 1)), on: n === k }));
  }
  if (q.t === "util") {
    const n = o === null || o === undefined ? 0 : UTILS.filter((x) => b.owner[x] === o).length;
    return [{ label: "Ejer 1 forsyning", value: "4 × terningerne", on: n === 1 }, { label: "Ejer begge", value: "10 × terningerne", on: n === 2 }];
  }
  return [];
}

export const mortgageValue = (i: number) => priceOf(i) / 2;
export const unmortgageCost = (i: number) => Math.ceil(priceOf(i) / 2 * 1.1);

/** explanations for the fields that cannot be bought */
export function fieldText(q: Sq): string {
  switch (q.t) {
    case "go": return "Hver gang du passerer START, får du 200 kr. fra banken.";
    case "chest": return "Land her, og træk et kort fra Fælleskassen. Det kan give penge – eller koste.";
    case "chance": return "Land her, og træk et Chancekort. Måske rykker du til et nyt felt!";
    case "tax": return `Du skal betale ${q.amount} kr. Svar rigtigt på et spørgsmål og betal kun det halve.`;
    case "jail": return "Er du bare på besøg, sker der ingenting. Er du fanget, kan du svare på et spørgsmål, betale 50 kr., bruge et løsladelseskort eller slå to ens.";
    case "parking": return "Her kan du hvile dig – der sker ingenting.";
    case "gojail": return "Land her, og du rykker direkte i fængsel uden at passere START.";
    default: return "";
  }
}

export function groupHeld(b: MonopolyView, i: number): { have: number; of: number } | null {
  const q = SQUARES[i]!;
  if (q.t !== "prop") return null;
  const o = b.owner[i];
  if (o === null || o === undefined) return null;
  const g = GROUPS[q.group];
  return { have: g.filter((x) => b.owner[x] === o).length, of: g.length };
}
